import { z } from 'zod';

const envSchema = z.object({
	VITE_API_URL: z
		.string()
		.optional()
		.transform((val) => {
			const trimmed = val?.trim() ?? '';
			return trimmed || '/api';
		}),
	VITE_ENVIRONMENT: z
		.string()
		.optional()
		.transform((val) => {
			const v = val?.trim()?.toLowerCase();

			if (!v) {
				return v;
			}

			if (v.startsWith('feature-')) {
				return 'feature';
			}

			return v;
		})
		.default('development')
		.pipe(
			z.enum([
				'development',
				'feature',
				'integration',
				'staging',
				'uat',
				'preprod',
				'production',
			]),
		),
	VITE_FIREBASE_API_KEY: z.string().optional(),
	VITE_FIREBASE_AUTH_DOMAIN: z.string().optional(),
	VITE_FIREBASE_PROJECT_ID: z.string().optional(),
	VITE_FIREBASE_AUTH_EMULATOR_URL: z.string().optional(),
	VITE_GOOGLE_MAPS_API_KEY: z.string().optional(),
	VITE_SENTRY_DSN: z.string().optional(),
	VITE_SENTRY_TRACES_SAMPLE_RATE: z.preprocess(
		(val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
		z.coerce.number().min(0).max(1).optional(),
	),
});

// Allow-listed so local and per-PR feature builds never report, and any new environment defaults to off.
// Keep in sync with sentryEnvs in the backend config and with vite.config.ts.
const SENTRY_ENVIRONMENTS: string[] = [
	'integration',
	'staging',
	'uat',
	'preprod',
	'production',
];

const parsedEnv = envSchema.parse(import.meta.env);
export const config = {
	apiUrl: parsedEnv.VITE_API_URL,
	firebase: {
		apiKey: parsedEnv.VITE_FIREBASE_API_KEY,
		authDomain: parsedEnv.VITE_FIREBASE_AUTH_DOMAIN,
		projectId: parsedEnv.VITE_FIREBASE_PROJECT_ID,
		// Set on local stacks to sign in against the Firebase Auth emulator instead of Google.
		authEmulatorUrl: parsedEnv.VITE_FIREBASE_AUTH_EMULATOR_URL,
	},
	enableSentry: SENTRY_ENVIRONMENTS.includes(parsedEnv.VITE_ENVIRONMENT),
	googleMapsApiKey: parsedEnv.VITE_GOOGLE_MAPS_API_KEY,
	VITE_ENVIRONMENT: parsedEnv.VITE_ENVIRONMENT,
	VITE_SENTRY_DSN: parsedEnv.VITE_SENTRY_DSN,
	sentryTracesSampleRate:
		parsedEnv.VITE_SENTRY_TRACES_SAMPLE_RATE ??
		(parsedEnv.VITE_ENVIRONMENT === 'production' ? 0.2 : 1.0),
};
